// Converts videos that some browsers cannot play to H.264 before they are uploaded.
//
// Phones record HEVC (H.265) or AV1 in .mp4/.mov containers. Chrome plays
// them, Edge does not (no AV1 decoder, HEVC needs a paid Store extension), so
// a file uploaded as-is shows a black frame in Edge for every viewer.
// Converting once here, on upload, spares every later viewer the in-page
// fallback in useVideoDecodeFallback.
//
// Detection reads only the container header (moov > trak > stsd), so files
// that are already H.264 - the vast majority - are uploaded untouched and
// without delay.

import { transcodeVideoBytes, type TranscodeOptions, type TranscodeProgress } from './videoTranscode';

// ISO-BMFF containers whose codec we can read and fix
const CONVERTIBLE_EXTENSIONS = ['.mp4', '.m4v', '.mov'];

// Sample entry codes every browser decodes
const PLAYABLE_CODECS = ['avc1', 'avc3'];

// H.264 profiles beyond High (10-bit / 4:2:2 / 4:4:4) that browsers reject
const UNPLAYABLE_AVC_PROFILES = [110, 122, 144, 244];

// moov is normally a few hundred KB; anything bigger is not worth parsing
const MAX_MOOV_BYTES = 64 * 1024 * 1024;

// The file is kept, so quality matters more than for the playback fallback:
// keep up to 1080p and use a slower preset than PLAYBACK_TRANSCODE
const UPLOAD_TRANSCODE: TranscodeOptions = {
  maxLongSide: 1920,
  preset: 'superfast',
  crf: 24,
  // Never holds up a video someone is waiting to watch
  lane: 'background',
};

export interface VideoCodecInfo {
  // Four-character sample entry code: 'avc1', 'hvc1', 'hev1', 'av01', ...
  codec: string;
  // AVCProfileIndication for H.264, otherwise null
  avcProfile: number | null;
}

interface Box {
  type: string;
  // Offsets relative to the buffer the box was read from
  start: number;
  headerSize: number;
  end: number;
}

function fourCC(view: DataView, offset: number): string
{
  return String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3),
  );
}

function readBoxHeader(view: DataView, offset: number, limit: number): Box | null
{
  if (offset + 8 > limit) return null;

  let size = view.getUint32(offset);
  const type = fourCC(view, offset + 4);
  let headerSize = 8;

  if (size === 1)
  {
    if (offset + 16 > limit) return null;
    size = Number(view.getBigUint64(offset + 8));
    headerSize = 16;
  }
  else if (size === 0)
  {
    size = limit - offset;
  }

  if (size < headerSize) return null;

  return { type, start: offset, headerSize, end: Math.min(offset + size, limit) };
}

function childBoxes(view: DataView, parent: Box, skip = 0): Box[]
{
  const boxes: Box[] = [];
  let offset = parent.start + parent.headerSize + skip;

  while (offset < parent.end)
  {
    const box = readBoxHeader(view, offset, parent.end);
    if (!box) break;
    boxes.push(box);
    offset = box.end;
  }

  return boxes;
}

function findChild(view: DataView, parent: Box, type: string, skip = 0): Box | undefined
{
  return childBoxes(view, parent, skip).find(b => b.type === type);
}

// Locates the top-level moov box by walking headers, without reading media data
async function readMoov(file: File): Promise<DataView | null>
{
  let offset = 0;

  while (offset + 8 <= file.size)
  {
    const header = new DataView(await file.slice(offset, offset + 16).arrayBuffer());
    const size32 = header.getUint32(0);
    const type = fourCC(header, 4);

    // 1: 64-bit size follows; 0: box runs to the end of the file
    let boxSize = size32;
    if (size32 === 1)
    {
      if (header.byteLength < 16) return null;
      boxSize = Number(header.getBigUint64(8));
    }
    else if (size32 === 0)
    {
      boxSize = file.size - offset;
    }
    if (boxSize < 8) return null;

    if (type === 'moov')
    {
      if (boxSize > MAX_MOOV_BYTES) return null;
      return new DataView(await file.slice(offset, offset + boxSize).arrayBuffer());
    }

    offset += boxSize;
  }

  return null;
}

function videoSampleEntry(view: DataView, trak: Box): VideoCodecInfo | null
{
  const mdia = findChild(view, trak, 'mdia');
  if (!mdia) return null;

  // hdlr: version/flags(4) + pre_defined(4) + handler_type(4)
  const hdlr = findChild(view, mdia, 'hdlr');
  if (!hdlr || fourCC(view, hdlr.start + hdlr.headerSize + 8) !== 'vide') return null;

  let box: Box | undefined = mdia;
  for (const type of ['minf', 'stbl', 'stsd'])
  {
    box = box && findChild(view, box, type);
  }
  if (!box) return null;

  // stsd: version/flags(4) + entry_count(4), then the sample entries
  const entry = readBoxHeader(view, box.start + box.headerSize + 8, box.end);
  if (!entry) return null;

  let avcProfile: number | null = null;
  if (PLAYABLE_CODECS.includes(entry.type))
  {
    // Visual sample entry fields take 78 bytes before its child boxes
    const avcC = findChild(view, entry, 'avcC', 78);
    if (avcC)
    {
      // configurationVersion(1), then AVCProfileIndication(1)
      avcProfile = view.getUint8(avcC.start + avcC.headerSize + 1);
    }
  }

  return { codec: entry.type, avcProfile };
}

/**
 * Reads the video codec from an MP4/MOV header. Returns null for files it
 * cannot parse (other containers, fragmented or truncated files).
 */
export async function detectVideoCodec(file: File): Promise<VideoCodecInfo | null>
{
  try
  {
    const view = await readMoov(file);
    if (!view) return null;

    const moov = readBoxHeader(view, 0, view.byteLength);
    if (!moov || moov.type !== 'moov') return null;

    for (const trak of childBoxes(view, moov).filter(b => b.type === 'trak'))
    {
      const info = videoSampleEntry(view, trak);
      if (info) return info;
    }

    return null;
  }
  catch
  {
    return null;
  }
}

function extensionOf(name: string): string
{
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
}

/**
 * True when `file` is a video whose codec some supported browser cannot play.
 * Unknown or unparsable files are left alone.
 */
export async function needsVideoConversion(file: File): Promise<boolean>
{
  if (!CONVERTIBLE_EXTENSIONS.includes(extensionOf(file.name))) return false;

  const info = await detectVideoCodec(file);
  if (!info) return false;

  // Encrypted or unknown entries are not ours to touch
  if (info.codec === 'encv') return false;

  if (PLAYABLE_CODECS.includes(info.codec))
  {
    return info.avcProfile !== null && UNPLAYABLE_AVC_PROFILES.includes(info.avcProfile);
  }

  // hvc1/hev1 (HEVC), av01 (AV1), vp09, mp4v, ...
  return true;
}

/**
 * Returns a file ready to upload: the original, or an H.264 MP4 copy when the
 * original would not play in every browser. Never throws - on any conversion
 * failure the original is returned so the upload still happens.
 */
export async function prepareVideoForUpload(
  file: File,
  onProgress?: (p: TranscodeProgress) => void,
): Promise<File>
{
  if (!(await needsVideoConversion(file))) return file;

  try
  {
    onProgress?.({ ratio: null, stage: 'loading-core' });

    const bytes = new Uint8Array(await file.arrayBuffer());
    const blob = await transcodeVideoBytes(bytes, UPLOAD_TRANSCODE, onProgress);

    const baseName = file.name.slice(0, file.name.length - extensionOf(file.name).length) || file.name;
    return new File([blob], `${baseName}.mp4`, { type: 'video/mp4', lastModified: file.lastModified });
  }
  catch (err)
  {
    console.warn('videoUploadPrep: conversion failed, uploading the original', err);
    return file;
  }
}
